import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Avatar,
  Button,
  List,
  ListItemButton,
  Skeleton,
  Menu,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TV2_COLUMN_HEADER_HEIGHT } from "../../../constants/ticketV2Constants";
import { IconDetailsSplit24, IconBulkCheck20 } from "./TicketV2Icons";
import { colorCodesForAvatar } from "../../../constants/index";
import { chracterPosition } from "../../../utils/index";
import { ticketShortId } from "../../../utils/ticketV2Mappers";
import TicketBulkActions from "./TicketBulkActions";

const inter = '"Inter", sans-serif';

const DEFAULT_STATUS_FILTER_OPTIONS = [
  { id: "open", label: "Open" },
  { id: "pending", label: "Pending" },
  { id: "solved", label: "Solved" },
];

const SORT_OPTIONS = [
  { id: "newest", label: "Newest", sort_by: null, order: "desc" },
  { id: "oldest", label: "Oldest", sort_by: null, order: "asc" },
  { id: "updated_desc", label: "Last updated", sort_by: "updatedAt", order: "desc" },
  { id: "updated_asc", label: "Oldest updated", sort_by: "updatedAt", order: "asc" },
];

function matchSortOption(sortBy, order) {
  const normalizedBy = sortBy || null;
  const normalizedOrder = order === "asc" ? "asc" : "desc";
  return (
    SORT_OPTIONS.find(
      (o) => (o.sort_by || null) === normalizedBy && o.order === normalizedOrder
    ) || SORT_OPTIONS[0]
  );
}

const headerRowSx = {
  height: TV2_COLUMN_HEADER_HEIGHT,
  minHeight: TV2_COLUMN_HEADER_HEIGHT,
  boxSizing: "border-box",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
  borderBottom: "1px solid #E5E9EC",
};

function ListRowSkeleton() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: "1px solid #F1F3F5",
        height: 96,
        boxSizing: "border-box",
      }}
    >
      <Skeleton variant="circular" width={36} height={36} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Skeleton width={48} height={14} />
          <Skeleton width={40} height={14} />
        </Stack>
        <Skeleton width="85%" height={18} sx={{ mb: 0.5 }} />
        <Skeleton width="70%" height={16} />
      </Box>
    </Stack>
  );
}

function formatRowTime(ticket, dateField = "createdAt") {
  const iso =
    dateField === "updatedAt"
      ? ticket?.updatedAt ?? ticket?.updated_at
      : ticket?.createdAt ?? ticket?.created_at;
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const days = Math.floor(diffMs / 86400000);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(diffMs / 3600000);
  if (hours >= 1) return `${hours}h`;
  const mins = Math.floor(diffMs / 60000);
  if (mins >= 1) return `${mins}m`;
  return "Now";
}

function BulkCheckbox({ checked }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: "16px",
        boxSizing: "border-box",
        bgcolor: checked ? "#F3FBFF" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {checked ? (
        <IconBulkCheck20 />
      ) : (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "4px",
            border: "1.5px solid #C5CED6",
            boxSizing: "border-box",
            bgcolor: "#fff",
          }}
        />
      )}
    </Box>
  );
}

/** Avatar slot: hover reveals select checkbox; in bulk mode checkbox replaces avatar. */
function TicketAvatarSlot({
  selectionMode,
  checked,
  name,
  avatarSrc,
  bg,
  initial,
  onSelectClick,
}) {
  if (selectionMode) {
    return (
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <BulkCheckbox checked={checked} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        position: "relative",
        borderRadius: "50%",
        overflow: "hidden",
        "&:hover .tv2-avatar-face": { opacity: 0 },
        "&:hover .tv2-avatar-select": { opacity: 1 },
      }}
    >
      {avatarSrc ? (
        <Avatar
          className="tv2-avatar-face"
          src={avatarSrc}
          alt={name}
          sx={{
            width: 36,
            height: 36,
            transition: "opacity 0.12s ease",
          }}
        />
      ) : (
        <Avatar
          className="tv2-avatar-face"
          sx={{
            width: 36,
            height: 36,
            bgcolor: bg,
            fontSize: 14,
            transition: "opacity 0.12s ease",
          }}
        >
          {initial}
        </Avatar>
      )}
      <Box
        className="tv2-avatar-select"
        role="button"
        aria-label="Select ticket"
        title="Select"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onSelectClick?.();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F3FBFF",
          cursor: "pointer",
          transition: "opacity 0.12s ease",
        }}
      >
        <BulkCheckbox checked={false} />
      </Box>
    </Box>
  );
}

function TicketList({
  tickets = [],
  loading = false,
  selectedId,
  onSelect,
  onToggleTicketsNav,
  headerTitle = "Tickets",
  statusFilter = "open",
  statusFilterCount = 0,
  statusFilterCountLoading = false,
  onStatusFilterChange,
  hideStatusFilter = false,
  statusFilterOptions = DEFAULT_STATUS_FILTER_OPTIONS,
  statusFilterStaticLabel = "",
  sortBy = null,
  sortOrder = "desc",
  onSortChange,
  listHasMore = false,
  onNearListEnd,
  searchQuery = "",
  onBulkApplied,
}) {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);

  const statusLabel =
    statusFilterOptions.find((o) => o.id === statusFilter)?.label || "Open";
  const staticStatusLabel = statusFilterStaticLabel || statusLabel;
  const statusCountLabel = statusFilterCountLoading ? (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        verticalAlign: "middle",
      }}
    >
      <Skeleton
        variant="rounded"
        width={18}
        height={14}
        sx={{ display: "inline-block", flexShrink: 0 }}
      />
      <Box component="span">{staticStatusLabel}</Box>
    </Box>
  ) : (
    `${statusFilterCount} ${staticStatusLabel}`
  );
  const statusFilterButtonLabel = statusFilterCountLoading ? (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        verticalAlign: "middle",
      }}
    >
      <Skeleton
        variant="rounded"
        width={18}
        height={14}
        sx={{ display: "inline-block", flexShrink: 0 }}
      />
      <Box component="span">{statusLabel}</Box>
    </Box>
  ) : (
    `${statusFilterCount} ${statusLabel}`
  );
  const activeSort = matchSortOption(sortBy, sortOrder);
  const sortLabel = activeSort.label;
  const statusMenuOpen =
    Boolean(statusMenuAnchor) &&
    !hideStatusFilter &&
    statusMenuAnchor.isConnected;
  const sortMenuOpen = Boolean(sortMenuAnchor) && sortMenuAnchor.isConnected;

  useEffect(() => {
    setStatusMenuAnchor(null);
    setSortMenuAnchor(null);
  }, [hideStatusFilter, headerTitle, statusFilter, sortBy, sortOrder]);

  // Drop bulk selections that left the current list (filter / pagination).
  useEffect(() => {
    if (!selectionMode) return;
    const visible = new Set(tickets.map((t) => t._id).filter(Boolean));
    setBulkSelected((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [tickets, selectionMode]);

  useEffect(() => {
    if (selectionMode && bulkSelected.length === 0) {
      setSelectionMode(false);
    }
  }, [selectionMode, bulkSelected.length]);

  const enterSelectionMode = useCallback((ticketId) => {
    setSelectionMode(true);
    setBulkSelected(ticketId ? [ticketId] : []);
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setBulkSelected([]);
  }, []);

  const toggleBulkId = useCallback((ticketId) => {
    if (!ticketId) return;
    setBulkSelected((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const ids = tickets.map((t) => t._id).filter(Boolean);
    setBulkSelected((prev) =>
      ids.length > 0 && prev.length >= ids.length ? [] : ids
    );
  }, [tickets]);

  const handleBulkApplied = useCallback(() => {
    exitSelectionMode();
    onBulkApplied?.();
  }, [exitSelectionMode, onBulkApplied]);

  const handleListScroll = useCallback(
    (e) => {
      if (!listHasMore || !onNearListEnd) return;
      const el = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < 180) {
        onNearListEnd();
      }
    },
    [listHasMore, onNearListEnd]
  );

  const handleStatusSelect = (nextStatus) => {
    setStatusMenuAnchor(null);
    if (nextStatus && nextStatus !== statusFilter) {
      onStatusFilterChange?.(nextStatus);
    }
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };

  const handleSortSelect = (option) => {
    setSortMenuAnchor(null);
    if (!option || option.id === activeSort.id) return;
    onSortChange?.({ sort_by: option.sort_by, order: option.order });
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleRowClick = (ticketId) => () => {
    if (selectionMode) {
      toggleBulkId(ticketId);
      return;
    }
    if (onSelect && ticketId) onSelect(ticketId);
  };

  const handleAvatarSelect = (ticketId) => () => {
    if (!ticketId) return;
    if (selectionMode) {
      toggleBulkId(ticketId);
      return;
    }
    enterSelectionMode(ticketId);
  };

  return (
    <Box
      component="section"
      sx={{
        width: 280,
        flexShrink: 0,
        borderRight: "1px solid #E5E9EC",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: inter,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={headerRowSx}>
        <Stack direction="row" alignItems="center" gap={1.25}>
          <IconButton
            size="small"
            onClick={onToggleTicketsNav}
            aria-label="Hide tickets sidebar"
            sx={{ p: 0.5, color: "#405261" }}
          >
            <IconDetailsSplit24 />
          </IconButton>

          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: "#061522",
              fontFamily: inter,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 200,
            }}
          >
            {headerTitle}
          </Typography>
        </Stack>
      </Stack>

      {selectionMode ? (
        <TicketBulkActions
          selectedIds={bulkSelected}
          tickets={tickets}
          onExit={exitSelectionMode}
          onSelectAll={handleSelectAll}
          onApplied={handleBulkApplied}
        />
      ) : (
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E9EC" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {hideStatusFilter ? (
              <Typography
                sx={{
                  color: "#061522",
                  fontWeight: 500,
                  fontSize: 13,
                  px: 0.75,
                  fontFamily: inter,
                }}
              >
                {statusCountLabel}
              </Typography>
            ) : (
              <Button
                size="small"
                endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                aria-haspopup="true"
                aria-expanded={statusMenuOpen}
                sx={{
                  color: "#061522",
                  fontWeight: 500,
                  fontSize: 14,
                  textTransform: "none",
                  minWidth: 0,
                  px: 0.75,
                  fontFamily: inter,
                }}
              >
                {statusFilterButtonLabel}
              </Button>
            )}
            <Menu
              anchorEl={statusMenuAnchor}
              open={statusMenuOpen}
              onClose={handleStatusMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    minWidth: 140,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    boxShadow: "0 8px 24px rgba(6, 21, 34, 0.08)",
                  },
                },
              }}
            >
              {statusFilterOptions.map((option) => (
                <MenuItem
                  key={option.id}
                  selected={option.id === statusFilter}
                  onClick={() => handleStatusSelect(option.id)}
                  sx={{
                    fontSize: 13,
                    fontFamily: inter,
                    color: "#061522",
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
            <Button
              size="small"
              endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              aria-haspopup="true"
              aria-expanded={sortMenuOpen}
              aria-label="Sort tickets"
              sx={{
                color: "#061522",
                fontWeight: 500,
                fontSize: 14,
                textTransform: "none",
                minWidth: 0,
                px: 0.75,
                fontFamily: inter,
              }}
            >
              {sortLabel}
            </Button>
            <Menu
              anchorEl={sortMenuAnchor}
              open={sortMenuOpen}
              onClose={handleSortMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    minWidth: 160,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    boxShadow: "0 8px 24px rgba(6, 21, 34, 0.08)",
                  },
                },
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem
                  key={option.id}
                  selected={option.id === activeSort.id}
                  onClick={() => handleSortSelect(option)}
                  sx={{
                    fontSize: 13,
                    fontFamily: inter,
                    color: "#061522",
                    "&.Mui-selected": {
                      color: "#061522",
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <List disablePadding sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <ListRowSkeleton key={i} />
            ))}
          </List>
        ) : tickets.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: "#8B97A2",
                fontFamily: inter,
                lineHeight: "20px",
              }}
            >
              {searchQuery.trim() ? "No tickets found" : "No tickets"}
            </Typography>
          </Box>
        ) : (
          <List
            disablePadding
            onScroll={handleListScroll}
            sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}
          >
            {tickets.map((t) => {
              const mongoId = t._id;
              const isActive = mongoId && mongoId === selectedId;
              const isChecked = selectionMode && bulkSelected.includes(mongoId);
              const sid = ticketShortId(t);
              const name = t.customerName || "—";
              const initial = name.substring(0, 1).toUpperCase();
              const bg =
                colorCodesForAvatar[chracterPosition(initial)] || "#757575";
              const preview =
                (typeof t.subject === "string" && t.subject) ||
                (typeof t.description === "string" ? t.description.slice(0, 72) : "") ||
                "—";
              const rowTime = formatRowTime(
                t,
                activeSort.sort_by === "updatedAt" ? "updatedAt" : "createdAt"
              );
              return (
                <ListItemButton
                  key={mongoId || sid}
                  onClick={handleRowClick(mongoId)}
                  sx={{
                    position: "relative",
                    gap: 1.5,
                    px: 2,
                    width: "100%",
                    maxWidth: "100%",
                    minHeight: 96,
                    height: 96,
                    boxSizing: "border-box",
                    borderBottom: "1px solid #F1F3F5",
                    borderRight: isActive ? "1px solid #148DFD" : "1px solid transparent",
                    alignItems: "center",
                    fontFamily: inter,
                    bgcolor: isActive ? "#F3FBFF" : "transparent",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    "&:hover": { bgcolor: isActive ? "#F3FBFF" : "#FAFBFC" },
                  }}
                >
                  <TicketAvatarSlot
                    selectionMode={selectionMode}
                    checked={isChecked}
                    name={name}
                    avatarSrc={t.customerAvatar || t.avatar}
                    bg={bg}
                    initial={initial}
                    onSelectClick={handleAvatarSelect(mongoId)}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography
                        sx={{
                          color: "#405261",
                          fontFamily: inter,
                          fontSize: "12px",
                          fontWeight: 400,
                          lineHeight: "18px",
                        }}
                      >
                        #{sid}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#405261",
                          fontFamily: inter,
                          fontSize: "12px",
                          fontWeight: 400,
                          lineHeight: "18px",
                        }}
                      >
                        {rowTime}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        color: "#061522",
                        fontFamily: inter,
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "18px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#061522",
                        fontFamily: inter,
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "18px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {preview}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}

export default TicketList;
